// mediaKit.jsx
import React, { useState, useEffect, useMemo, useRef } from 'react';
import {
  Backdrop,
  Box,
  Button,
  CircularProgress,
  CssBaseline,
  Grid,
  List,
  Paper,
  Rating,
  Toolbar,
  Typography,
  ListItem,
  ListItemText,
  Container,
  Stack,
  Fade,
  Zoom,
  Chip,
  Avatar,
  Snackbar,
  Alert,
  ClickAwayListener,
  Popper,
  Grow,
  MenuList,
  MenuItem,
} from '@mui/material';
import { lightTheme, darkTheme } from '../../Utils/baseTheme';
import HomeHeader from '../../Components/HomeHeader';
import PlaceIcon from '@mui/icons-material/Place';
import TikTokIcon from '../../Components/Icons/TikTok';
import InstagramBrandIcon from '../../Components/Icons/Instagram';
import { useNavigate, useParams } from 'react-router-dom';
import {
  mapPlatformsToAudience,
  mapPlatformsToEngagement,
  platformRatesListFromCreator,
  platforms,
  platformToFollowing
} from '../../Utils/constants';
import API from '../../API';
import PageTitle from '../../Components/PageTitle';
import BookingDialog from '../Misc/bookingdialog';
import LeaveReview from '../Dashboard/Creators/leaveReview';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import VerifiedIcon from '@mui/icons-material/Verified';
import {
  YouTube as YoutubeIcon,
  Twitter as TwitterIcon,
  SwapHoriz as SwapHorizIcon,
  CardGiftcard as CardGiftcardIcon,
  RequestPage as RequestPageIcon,
  KeyboardArrowDown as KeyboardArrowDownIcon,
} from "@mui/icons-material";
import PodcastsIcon from '@mui/icons-material/Podcasts';
import CastIcon from '@mui/icons-material/Cast';
import TwitchIcon from '../../Components/Icons/Twitch';
import ShareIcon from '@mui/icons-material/Share';
import useAlertDialog from '../../Components/useAlertDialog';
import AlertDialog from '../../Components/AlertDialog';
import defaultPfp from '../../Components/globalAssets/ppfLogo.png';
import { alpha, styled, ThemeProvider } from '@mui/material/styles';
import ModernPlatformCard from './Components/platformCard';
import ModernCountryCard from './Components/countryCard';
import ModernGenderCard from './Components/genderCard';
import ModernEngagementCard from './Components/engagementCard';
import { useTheme } from '@mui/material/styles';
import useAuth from '../../Hooks/use-auth';
import { useCreatorAuth } from '../../Hooks/creator-use-auth';
import T2AUniversalDialog from '../../GlobalPages/globalUser/UniversalT2A';

// Theme selection
const getAppropriateTheme = (creator) => {
  if (creator && creator.creator && creator.creator.tier_level === 'celebrity') {
    return darkTheme;
  }
  return lightTheme;
};

// Styled components
const StyledRating = styled(Rating)(({ theme }) => ({
  '& .MuiRating-iconFilled': {
    color: theme.palette.mode === 'dark' ? '#FFD700' : '#FFD700',
  },
  '& .MuiRating-iconHover': {
    color: theme.palette.mode === 'dark' ? '#FFA500' : '#FFA500',
  },
  cursor: 'pointer',
  '&:hover': { opacity: 0.8 }
}));

const EnhancedBookButton = styled(Button)(({ theme }) => ({
  background: '#111827',
  color: '#FFFFFF',
  border: '1px solid #111827',
  borderRadius: '999px',
  fontWeight: 700,
  height: '46px',
  padding: '0 28px',
  fontSize: '0.95rem',
  textTransform: 'none',
  boxShadow: '0 10px 22px rgba(17, 24, 39, 0.16)',
  transition: 'all 0.2s ease',
  '&:hover': {
    background: '#0B1220',
    borderColor: '#0B1220',
    transform: 'translateY(-1px)',
    boxShadow: '0 14px 26px rgba(17, 24, 39, 0.2)',
  },
}));

const ShareButton = styled(Button)(({ theme }) => ({
  borderColor: '#D1D5DB',
  color: '#111827',
  backgroundColor: '#FFFFFF',
  borderRadius: '999px',
  height: '46px',
  padding: '0 24px',
  fontWeight: 600,
  fontSize: '0.95rem',
  textTransform: 'none',
  transition: 'all 0.2s ease',
  '&:hover': {
    borderColor: '#9CA3AF',
    backgroundColor: '#F9FAFB',
    transform: 'translateY(-1px)',
  }
}));

// ── Unified Transfer Button ───────────────────────────────────────────────────
const TransferButton = styled(Button)(({ theme }) => ({
  background: 'linear-gradient(135deg, #7c3aed 0%, #4f46e5 100%)',
  color: '#FFFFFF',
  borderRadius: '999px',
  height: '46px',
  padding: '0 22px 0 26px',
  fontWeight: 700,
  fontSize: '0.95rem',
  textTransform: 'none',
  boxShadow: '0 10px 24px rgba(124, 58, 237, 0.28)',
  transition: 'all 0.22s ease',
  gap: '6px',
  '&:hover': {
    background: 'linear-gradient(135deg, #6d28d9 0%, #4338ca 100%)',
    transform: 'translateY(-1px)',
    boxShadow: '0 14px 30px rgba(124, 58, 237, 0.36)',
  },
  '&:active': {
    transform: 'translateY(0)',
  },
}));

const TransferMenu = styled(Paper)(({ theme }) => ({
  borderRadius: '16px',
  border: '1px solid rgba(124, 58, 237, 0.18)',
  boxShadow: '0 20px 60px rgba(0,0,0,0.14), 0 4px 16px rgba(124,58,237,0.12)',
  overflow: 'hidden',
  minWidth: '220px',
  backdropFilter: 'blur(16px)',
  background: theme.palette.mode === 'dark'
    ? 'rgba(20,20,30,0.97)'
    : 'rgba(255,255,255,0.98)',
}));

const TransferMenuItem = styled(MenuItem)(({ theme, itemcolor }) => ({
  padding: '10px 14px',
  margin: '2px 8px',
  borderRadius: '14px',
  display: 'flex',
  alignItems: 'center',
  gap: '14px',
  fontWeight: 600,
  fontSize: '0.95rem',
  color: theme.palette.mode === 'dark' ? '#FFFFFF' : '#111827',
  transition: 'all 0.18s ease',
  '&:hover': {
    backgroundColor: itemcolor
      ? alpha(itemcolor, 0.1)
      : alpha(theme.palette.primary.main, 0.08),
    color: itemcolor || theme.palette.primary.main,
    '& .transfer-icon-wrap': {
      transform: 'scale(1.12)',
    },
  },
  '& .transfer-icon-wrap': {
    width: 38,
    height: 38,
    borderRadius: '50%',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    background: itemcolor ? alpha(itemcolor, 0.12) : alpha(theme.palette.primary.main, 0.1),
    color: itemcolor || theme.palette.primary.main,
    transition: 'transform 0.18s ease',
    flexShrink: 0,
  },
}));

// ─────────────────────────────────────────────────────────────────────────────

const HeroSection = styled(Box)(({ theme, isCelebrity }) => ({
  position: 'relative',
  minHeight: '280px',
  background: isCelebrity
    ? 'linear-gradient(180deg, #141414 0%, #101010 100%)'
    : 'linear-gradient(180deg, #FFFFFF 0%, #F8FAFC 100%)',
  display: 'flex',
  alignItems: 'center',
  overflow: 'hidden',
  borderBottom: isCelebrity ? '1px solid rgba(255,255,255,0.08)' : '1px solid #E5E7EB',
  '&::before': {
    content: '""',
    position: 'absolute',
    top: 0, left: 0, right: 0, bottom: 0,
    background: isCelebrity
      ? 'radial-gradient(circle at 30% 30%, rgba(99, 102, 241, 0.08) 0%, transparent 48%)'
      : 'radial-gradient(circle at 50% 0%, rgba(148, 163, 184, 0.12) 0%, transparent 40%)',
    pointerEvents: 'none',
  }
}));

const ProfileImage = styled('img')(({ theme }) => ({
  width: 108,
  height: 108,
  objectFit: 'cover',
  borderRadius: '50%',
  border: `4px solid ${theme.palette.mode === 'dark' ? '#1A1A1A' : '#FFFFFF'}`,
  boxShadow: '0 12px 28px rgba(15, 23, 42, 0.12)',
  transition: 'all 0.3s ease-in-out',
  position: 'relative',
  zIndex: 2,
  [theme.breakpoints.down('md')]: {
    width: 88,
    height: 88,
  }
}));

const SectionHeader = styled(Typography)(({ theme }) => ({
  fontWeight: 800,
  marginBottom: theme.spacing(2),
  fontSize: '1.7rem',
  color: theme.palette.text.primary,
  [theme.breakpoints.down('sm')]: { fontSize: '1.35rem' }
}));

const getHeroTextColor = (isCelebrity) => isCelebrity ? '#FFFFFF' : '#111827';
const getHeroSubtleTextColor = (isCelebrity) => isCelebrity ? 'rgba(255,255,255,0.86)' : '#4B5563';

const StatsCard = ({ icon, label, value, color }) => {
  const theme = useTheme();
  const isDarkMode = theme.palette.mode === 'dark';
  return (
    <Paper elevation={0} sx={{
      p: 2.75, textAlign: 'left', borderRadius: 3,
      border: `1px solid ${isDarkMode ? '#2A2A2A' : '#E5E7EB'}`,
      backgroundColor: isDarkMode ? '#1A1A1A' : '#FFFFFF',
      transition: 'all 0.3s ease', height: '100%', position: 'relative', overflow: 'hidden',
      '&:hover': { transform: 'translateY(-2px)', boxShadow: isDarkMode ? '0 10px 24px rgba(0,0,0,0.3)' : '0 10px 24px rgba(15,23,42,0.06)', borderColor: isDarkMode ? '#343434' : '#D1D5DB', '&::after': { opacity: 1 } },
      '&::after': { content: '""', position: 'absolute', inset: 0, background: isDarkMode ? 'radial-gradient(circle at top right, rgba(255,255,255,0.04), transparent 52%)' : 'radial-gradient(circle at top right, rgba(15,23,42,0.04), transparent 54%)', opacity: 0, transition: 'opacity 0.25s ease', pointerEvents: 'none' },
    }}>
      <Avatar sx={{ width: 38, height: 38, backgroundColor: isDarkMode ? 'rgba(255,255,255,0.06)' : '#F3F4F6', color: isDarkMode ? '#D1D5DB' : '#4B5563', mb: 1.75 }}>
        {React.cloneElement(icon, { sx: { fontSize: 18 } })}
      </Avatar>
      <Typography variant="h4" fontWeight={800} color={isDarkMode ? '#FFFFFF' : '#111827'} mb={0.5} sx={{ fontSize: { xs: '1.45rem', md: '1.8rem' }, letterSpacing: '-0.03em' }}>{value}</Typography>
      <Typography variant="body2" color="text.secondary" fontWeight={600}>{label}</Typography>
    </Paper>
  );
};

// Platform data
const AudienceCardsData = [
  { label: 'Youtube',  followingKey: 'youtube',       linkKey: 'youtube_link',  icon: <YoutubeIcon /> },
  { label: 'Instagram',followingKey: 'instagram',     linkKey: 'instagram_link',icon: <InstagramBrandIcon /> },
  { label: 'TikTok',   followingKey: 'tiktok',        linkKey: 'tiktok_link',   icon: <TikTokIcon /> },
  { label: 'Podcasts', followingKey: 'follow_podcast', linkKey: 'podcast_link',  icon: <PodcastsIcon /> },
  { label: 'Twitch',   followingKey: 'twitch',        linkKey: 'twitch_link',   icon: <TwitchIcon /> },
  { label: 'X',        followingKey: 'follow_x',      linkKey: 'x_link',        icon: <TwitterIcon /> },
  { label: 'Kick',     followingKey: 'follow_kick',   linkKey: 'kick_link',     icon: <CastIcon /> }
];

function ModernRateCard({ rate, label, theme }) {
  const isDarkMode = theme.palette.mode === 'dark';
  return (
    <ListItem disableGutters sx={{ py: 1.5, borderBottom: `1px solid ${alpha(theme.palette.divider, 0.1)}`, '&:last-child': { borderBottom: 'none' } }}
      secondaryAction={<Chip label={`$${rate.toLocaleString()}`} sx={{ background: isDarkMode ? '#23272F' : '#EEF2F7', border: `1px solid ${isDarkMode ? '#3B4251' : '#D8E0EA'}`, color: isDarkMode ? '#FFFFFF' : '#111827', fontWeight: 700, fontSize: '0.95rem', height: 34, px: 2 }} />}>
      <ListItemText primary={label} primaryTypographyProps={{ fontWeight: 600, fontSize: '1rem', color: isDarkMode ? '#FFFFFF' : '#1A1A1A' }} />
    </ListItem>
  );
}

const SectionSurface = React.forwardRef(function SectionSurface(
  { title, subtitle, children, emptyMessage, isEmpty = false }, ref
) {
  const theme = useTheme();
  const isDarkMode = theme.palette.mode === 'dark';
  return (
    <Paper ref={ref} elevation={0} sx={{
      p: { xs: 2.75, md: 3.25 }, borderRadius: 4,
      border: `1px solid ${isDarkMode ? '#242424' : '#E5E7EB'}`,
      backgroundColor: isDarkMode ? '#141414' : '#FFFFFF',
      transition: 'box-shadow 0.25s ease, border-color 0.25s ease, transform 0.25s ease',
      '&:hover': { transform: 'translateY(-1px)', boxShadow: isDarkMode ? '0 14px 30px rgba(0,0,0,0.18)' : '0 14px 30px rgba(15,23,42,0.04)', borderColor: isDarkMode ? '#2F2F2F' : '#D1D5DB' },
    }}>
      <Box sx={{ mb: 2.5 }}>
        <SectionHeader variant="h4" sx={{ mb: 0.75, fontSize: { xs: '1.4rem', md: '1.55rem' } }}>{title}</SectionHeader>
        {subtitle && <Typography variant="body2" color="text.secondary" sx={{ maxWidth: 680, lineHeight: 1.55 }}>{subtitle}</Typography>}
      </Box>
      {isEmpty ? (
        <Box sx={{ borderRadius: 3, border: `1px dashed ${isDarkMode ? '#333333' : '#D6DFEA'}`, backgroundColor: isDarkMode ? 'rgba(255,255,255,0.02)' : '#F8FAFC', px: 2.5, py: 3 }}>
          <Typography variant="body1" fontWeight={600} color="text.primary">{emptyMessage || 'Not configured yet'}</Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 0.75 }}>This section is available, but the creator does not currently have enough data configured for it.</Typography>
        </Box>
      ) : children}
    </Paper>
  );
});

// ── Transfer Button with Dropdown ─────────────────────────────────────────────
function TransferDropdownButton({ onGift, onRequest }) {
  const [open, setOpen] = useState(false);
  const anchorRef = useRef(null);

  const handleToggle = () => setOpen((prev) => !prev);
  const handleClose = (event) => {
    if (anchorRef.current && anchorRef.current.contains(event.target)) return;
    setOpen(false);
  };

  return (
    <>
      <TransferButton
        ref={anchorRef}
        onClick={handleToggle}
        startIcon={<SwapHorizIcon sx={{ fontSize: '1.15rem' }} />}
        endIcon={
          <KeyboardArrowDownIcon
            sx={{
              fontSize: '1.1rem',
              transition: 'transform 0.22s ease',
              transform: open ? 'rotate(180deg)' : 'rotate(0deg)',
            }}
          />
        }
      >
        Transfer
      </TransferButton>

      <Popper
        open={open}
        anchorEl={anchorRef.current}
        placement="bottom-start"
        transition
        disablePortal={false}
        style={{ zIndex: 1400 }}
      >
        {({ TransitionProps }) => (
          <Grow
            {...TransitionProps}
            style={{ transformOrigin: 'top left' }}
            timeout={180}
          >
            <TransferMenu elevation={0}>
              <ClickAwayListener onClickAway={handleClose}>
                <MenuList disablePadding sx={{ py: 1.5 }}>
                  {/* Divider label */}
                  <Box sx={{ px: 3, pt: 0.5, pb: 1 }}>
                    <Typography variant="caption" sx={{ fontWeight: 700, letterSpacing: '0.08em', textTransform: 'uppercase', color: 'text.disabled', fontSize: '0.68rem' }}>
                      Choose action
                    </Typography>
                  </Box>

                  {/* Send / Gift */}
                  <TransferMenuItem
                    itemcolor="#7c3aed"
                    onClick={() => { setOpen(false); onGift(); }}
                  >
                    <Box className="transfer-icon-wrap">
                      <CardGiftcardIcon sx={{ fontSize: '1.1rem' }} />
                    </Box>
                    <Box>
                      <Typography variant="body2" fontWeight={700} lineHeight={1.2}>Send Balance</Typography>
                      <Typography variant="caption" color="text.secondary" lineHeight={1.4}>Gift funds instantly</Typography>
                    </Box>
                  </TransferMenuItem>

                  {/* Request */}
                  <TransferMenuItem
                    itemcolor="#10b981"
                    onClick={() => { setOpen(false); onRequest(); }}
                  >
                    <Box className="transfer-icon-wrap" sx={{ background: `${alpha('#10b981', 0.12)} !important`, color: '#10b981 !important' }}>
                      <RequestPageIcon sx={{ fontSize: '1.1rem' }} />
                    </Box>
                    <Box>
                      <Typography variant="body2" fontWeight={700} lineHeight={1.2}>Request Balance</Typography>
                      <Typography variant="caption" color="text.secondary" lineHeight={1.4}>Ask for payment</Typography>
                    </Box>
                  </TransferMenuItem>
                </MenuList>
              </ClickAwayListener>
            </TransferMenu>
          </Grow>
        )}
      </Popper>
    </>
  );
}
// ─────────────────────────────────────────────────────────────────────────────

export default function MediaKit({
  initialCreatorData = null,
  creatorIdFromParams = null,
  hideHeader = false
}) {
  const { creatorId: paramCreatorId, discount } = useParams();
  const creatorId = creatorIdFromParams || paramCreatorId;

  const [creatorData, setCreatorData] = useState(initialCreatorData);
  const [loading, setLoading] = useState(!initialCreatorData);
  const [error, setError] = useState(null);
  const [bookingDialogOpen, setBookingDialogOpen] = useState(false);
  const [reviewDialogOpen, setReviewDialogOpen] = useState(false);
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState('');
  const [snackbarSeverity, setSnackbarSeverity] = useState('success');

  const { dialogState, openDialog, closeDialog } = useAlertDialog();
  const navigate = useNavigate();

  // ── Auth — to show/hide T2A buttons ──────────────────────────────────────
  const { isAuthorized: isUserAuthorized } = useAuth();
  const { isAuthorized: isCreatorAuthorized } = useCreatorAuth();
  const isViewerAuthorized = isUserAuthorized || isCreatorAuthorized;

  // ── T2A dialog state ──────────────────────────────────────────────────────
  const [t2aOpen, setT2aOpen] = useState(false);
  const [t2aMode, setT2aMode] = useState("gift");

  const resolveRecipientGUId = null;

  useEffect(() => {
    if (initialCreatorData) {
      setCreatorData(initialCreatorData);
      setLoading(false);
      return;
    }
    if (!creatorId) { setLoading(false); return; }
    const fetchCreatorData = async () => {
      setLoading(true);
      setError(null);
      try {
        const result = await API.creators.fetchDetails(creatorId);
        setCreatorData(result);
        setLoading(false);
      } catch (err) {
        setError(err);
        setLoading(false);
      }
    };
    fetchCreatorData();
  }, [creatorId, initialCreatorData]);

  useEffect(() => {
    if (creatorData && !loading && initialCreatorData) setBookingDialogOpen(true);
  }, [creatorData, loading, initialCreatorData]);

  useEffect(() => {
    if (error) {
      openDialog(
        'Error Loading Creator',
        <>{`Error while loading creator profile:\n${error?.response?.data?.error || error.message}`}</>,
        () => { closeDialog(); navigate('/'); },
        closeDialog, 'Go Home', 'Close'
      );
    }
  }, [error, openDialog, closeDialog, navigate]);

  const totalFollowers = useMemo(() => {
    if (!creatorData) return 0;
    return platforms.reduce((prev, platform) => prev + parseInt(creatorData.creator[platformToFollowing[platform]] || '0', 10), 0);
  }, [creatorData]);

  const isCelebrity = creatorData?.creator?.tier_level === 'celebrity';

  const rateList = useMemo(() => {
    if (!creatorData?.creator) return [];
    return platformRatesListFromCreator(creatorData.creator).filter(rate => rate.rate && rate.rate > 0);
  }, [creatorData]);

  const audienceList = useMemo(() => {
    if (!creatorData?.creator) return [];
    return mapPlatformsToAudience(AudienceCardsData, creatorData.creator).filter(a => parseInt(a.following || '0', 10) > 0);
  }, [creatorData]);

  const engagementList = useMemo(() => {
    if (!creatorData?.creator) return [];
    return mapPlatformsToEngagement(AudienceCardsData, creatorData.creator).filter(e => e.value && e.value !== 0 && e.value !== 'N/A');
  }, [creatorData]);

  const countryDemos = useMemo(() => {
    if (!creatorData?.creator?.public) return [];
    const relevantCountryDemos = creatorData.creator.public.map((demo) => demo.country_data || []).filter((demo) => Array.isArray(demo) && demo.length > 0);
    if (relevantCountryDemos.length === 0) return [];
    const aggregated = relevantCountryDemos.reduce((acc, demo) => {
      demo.forEach((entry) => { if (entry.value > 0) acc[entry.code] = (acc[entry.code] || 0) + entry.value; });
      return acc;
    }, {});
    return Object.entries(aggregated).map(([code, value]) => ({ code, value: value / relevantCountryDemos.length })).sort((a, b) => b.value - a.value).slice(0, 8);
  }, [creatorData]);

  const genderDemos = useMemo(() => {
    if (!creatorData?.creator?.public) return [];
    const relevantAudienceDemos = creatorData.creator.public.map((demo) => demo.audience_data || []).filter((demo) => Array.isArray(demo) && demo.length > 0);
    if (relevantAudienceDemos.length === 0) return [];
    const allData = relevantAudienceDemos.flat();
    return [
      { name: 'Male', value: allData.reduce((sum, d) => d.gender.toLowerCase() === 'male' ? sum + d.value : sum, 0) / relevantAudienceDemos.length },
      { name: 'Female', value: allData.reduce((sum, d) => d.gender.toLowerCase() === 'female' ? sum + d.value : sum, 0) / relevantAudienceDemos.length },
    ].filter(g => g.value > 0);
  }, [creatorData]);

  const formatNumber = (num) => {
    if (num >= 1000000) return `${(num / 1000000).toFixed(1)}M`;
    if (num >= 1000) return `${(num / 1000).toFixed(0)}K`;
    return num.toLocaleString();
  };

  const avgReview = creatorData?.average_rating || 0;
  const reviewCount = creatorData?.review_count || 0;
  const hasReviews = avgReview > 0 && reviewCount > 0;
  const hasStatsOverview = totalFollowers > 0 || audienceList.length > 0 || hasReviews;
  const hasAnalytics = engagementList.length > 0 || countryDemos.length > 0 || genderDemos.length > 0;

  const statsCards = [
    totalFollowers > 0 ? { key: 'followers', icon: <TikTokIcon />, label: 'Total Followers', value: formatNumber(totalFollowers), color: '#4F46E5' } : null,
    audienceList.length > 0 ? { key: 'platforms', icon: <InstagramBrandIcon />, label: 'Active Platforms', value: audienceList.length, color: '#059669' } : null,
    hasReviews ? { key: 'rating', icon: <CheckCircleIcon />, label: 'Average Rating', value: avgReview.toFixed(1), color: '#D97706' } : null,
    hasReviews ? { key: 'reviews', icon: <VerifiedIcon />, label: 'Total Reviews', value: reviewCount, color: '#B45309' } : null,
  ].filter(Boolean);

  const appliedTheme = getAppropriateTheme(creatorData);
  const isDarkMode = appliedTheme.palette.mode === 'dark';

  const handleShare = async () => {
    const url = window.location.href;
    try {
      if (navigator.share) {
        await navigator.share({ title: `Check out @${creatorId} on Blitz`, text: `Book collaborations with @${creatorId}`, url });
        setSnackbarMessage('Profile shared successfully!');
      } else if (navigator.clipboard?.writeText) {
        await navigator.clipboard.writeText(url);
        setSnackbarMessage('Profile link copied to clipboard!');
      } else {
        const tempInput = document.createElement('input');
        tempInput.value = url;
        document.body.appendChild(tempInput);
        tempInput.select();
        tempInput.setSelectionRange(0, 99999);
        const successful = document.execCommand('copy');
        document.body.removeChild(tempInput);
        setSnackbarMessage(successful ? 'Profile link copied to clipboard!' : 'Failed to share. Please copy the URL manually.');
        setSnackbarSeverity(successful ? 'success' : 'error');
        setSnackbarOpen(true);
        return;
      }
      setSnackbarSeverity('success');
      setSnackbarOpen(true);
    } catch (err) {
      setSnackbarMessage('Failed to share. Please copy the URL manually.');
      setSnackbarSeverity('error');
      setSnackbarOpen(true);
    }
  };

  if (!creatorId) {
    return (
      <ThemeProvider theme={lightTheme}>
        <CssBaseline />
        {!hideHeader && <HomeHeader />}
        {!hideHeader && <Toolbar />}
        <Container maxWidth="md" sx={{ py: 8, textAlign: 'center' }}>
          <Typography variant="h4" color="error" gutterBottom>Missing Creator ID</Typography>
          <Typography variant="body1" color="text.secondary">No creator identifier was provided in the URL.</Typography>
          <Button variant="contained" onClick={() => navigate('/')} sx={{ mt: 3 }}>Go Home</Button>
        </Container>
      </ThemeProvider>
    );
  }

  return (
    <ThemeProvider theme={appliedTheme}>
      <CssBaseline />
      {!hideHeader && <HomeHeader />}
      <AlertDialog alertState={dialogState} />
      {!hideHeader && <Toolbar />}

      {discount && (
        <Box sx={{ backgroundColor: '#FF9500', color: '#FFF', textAlign: 'center', padding: '12px', fontWeight: 700, fontSize: '1.1rem', position: 'fixed', top: hideHeader ? '0px' : '64px', width: '100%', zIndex: 1100, boxShadow: '0 4px 20px rgba(255, 149, 0, 0.4)' }}>
          🎉 SPECIAL OFFER: BOOK TODAY FOR {discount}% OFF! 🎉
        </Box>
      )}

      <Backdrop sx={{ color: '#fff', zIndex: 9999 }} open={loading}>
        <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 2 }}>
          <CircularProgress color="inherit" size={60} />
          <Typography variant="h6">Loading Creator Profile...</Typography>
        </Box>
      </Backdrop>

      <PageTitle title={`Book @${creatorId} - Blitz`} />

      {/* Hero Section */}
      <HeroSection isCelebrity={isCelebrity}>
        <Container maxWidth="lg" sx={{ py: { xs: 3.5, md: 4 } }}>
          <Fade in timeout={800}>
            <Box textAlign="center">
              <ProfileImage src={creatorData?.creator.pfphref || defaultPfp} alt="Profile" />

              <Box sx={{ mt: 2 }}>
                <Stack direction="row" alignItems="center" justifyContent="center" spacing={1.25} mb={1.5}>
                  <Typography variant="h2" sx={{ fontWeight: 800, color: getHeroTextColor(isCelebrity), fontSize: { xs: '1.7rem', sm: '2rem', md: '2.25rem' }, letterSpacing: '-0.04em', textShadow: isCelebrity ? '0 4px 20px rgba(0,0,0,0.3)' : 'none' }}>
                    @{creatorId}
                  </Typography>
                  {creatorData?.creator.is_verified && <VerifiedIcon sx={{ fontSize: 32, color: '#4FC3F7' }} />}
                </Stack>

                <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} justifyContent="center" alignItems="center" mb={2.25}>
                  {creatorData?.creator.geolocation && (
                    <Chip icon={<PlaceIcon sx={{ color: `${getHeroSubtleTextColor(isCelebrity)} !important` }} />} label={`${creatorData.creator.geolocation} (${creatorData.creator.region})`}
                      sx={{ backgroundColor: isCelebrity ? 'rgba(255,255,255,0.12)' : '#FFFFFF', border: isCelebrity ? '1px solid rgba(255,255,255,0.18)' : '1px solid #E5E7EB', color: getHeroSubtleTextColor(isCelebrity), fontWeight: 600, fontSize: '0.9rem', height: 32 }} />
                  )}
                  {creatorData?.creator.primary_markets?.length > 0 && (
                    <Chip label={creatorData.creator.primary_markets.join(', ')}
                      sx={{ backgroundColor: isCelebrity ? 'rgba(255,255,255,0.12)' : '#FFFFFF', border: isCelebrity ? '1px solid rgba(255,255,255,0.18)' : '1px solid #E5E7EB', color: getHeroSubtleTextColor(isCelebrity), fontWeight: 600, fontSize: '0.9rem', height: 32 }} />
                  )}
                </Stack>

                {creatorData?.creator.notes_content_style && (
                  <Typography variant="h6" sx={{ color: getHeroSubtleTextColor(isCelebrity), maxWidth: 760, mx: 'auto', mb: 2.25, lineHeight: 1.6, fontSize: { xs: '0.92rem', sm: '0.98rem' }, textShadow: isCelebrity ? '0 2px 10px rgba(0,0,0,0.2)' : 'none' }}>
                    {creatorData.creator.notes_content_style}
                  </Typography>
                )}

                {/* ── Action Buttons ──────────────────────────────────────── */}
                <Stack
                  direction={{ xs: 'column', sm: 'row' }}
                  spacing={1.25}
                  justifyContent="center"
                  alignItems="center"
                  flexWrap="wrap"
                  mb={hasReviews && !isCelebrity ? 1.75 : 0}
                >
                  <EnhancedBookButton onClick={() => setBookingDialogOpen(true)} disabled={loading}>
                    Book Collaboration
                  </EnhancedBookButton>

                  <ShareButton variant="outlined" startIcon={<ShareIcon />} onClick={handleShare}>
                    Share Profile
                  </ShareButton>

                  {/* Single Transfer button — only for authorized viewers */}
                  {isViewerAuthorized && (
                    <TransferDropdownButton
                      onGift={() => { setT2aMode("gift"); setT2aOpen(true); }}
                      onRequest={() => { setT2aMode("request"); setT2aOpen(true); }}
                    />
                  )}
                </Stack>

                {!isCelebrity && hasReviews && (
                  <Stack direction="row" alignItems="center" justifyContent="center" spacing={1}>
                    <StyledRating value={Math.round(avgReview)} onClick={() => setReviewDialogOpen(true)} readOnly size="large" />
                    <Typography variant="body1" sx={{ color: getHeroSubtleTextColor(isCelebrity), fontWeight: 600 }}>
                      {avgReview.toFixed(1)} ({reviewCount} reviews)
                    </Typography>
                  </Stack>
                )}
              </Box>
            </Box>
          </Fade>
        </Container>
      </HeroSection>

      {/* Content Section */}
      <Container maxWidth="lg" sx={{ py: { xs: 3.25, md: 4 }, position: 'relative', zIndex: 10 }}>
        {hasStatsOverview && (
          <Fade in timeout={1000}>
            <Box mb={4}>
              <SectionSurface title="Overview" subtitle="A quick summary of the creator profile currently configured in Blitz.">
                <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(170px, 1fr))', gap: 2 }}>
                  {statsCards.map((card) => <StatsCard key={card.key} icon={card.icon} label={card.label} value={card.value} color={card.color} />)}
                </Box>
              </SectionSurface>
            </Box>
          </Fade>
        )}

        <Box mb={4}>
          <SectionSurface title="Social Media Presence" subtitle="Configured platform profiles and the latest saved audience signals for each one." isEmpty={audienceList.length === 0} emptyMessage="No social platforms are configured yet.">
            <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(240px, 1fr))', gap: 2 }}>
              {audienceList.map((audience, index) => (
                <Zoom in timeout={300 + index * 100} key={index}>
                  <Box><ModernPlatformCard handle={audience.handle} link={audience.link} following={audience.following} views={audience.average_views} icon={audience.icon} /></Box>
                </Zoom>
              ))}
            </Box>
          </SectionSurface>
        </Box>

        {!isCelebrity && (
          <Fade in timeout={1200}>
            <Box mb={4}>
              <SectionSurface title="Collaboration Rates" subtitle="Current configured pricing pulled from the creator profile." isEmpty={rateList.length === 0} emptyMessage="No collaboration rates are configured yet.">
                <List sx={{ py: 0 }}>
                  {rateList.map((rate, index) => <ModernRateCard key={index} label={rate.label} rate={rate.rate} theme={appliedTheme} />)}
                </List>
              </SectionSurface>
            </Box>
          </Fade>
        )}

        <Box mb={4}>
          <SectionSurface title="Audience Analytics" subtitle="Saved demographic and engagement snapshots from connected creator data." isEmpty={!hasAnalytics} emptyMessage="Audience analytics are not available for this creator yet.">
            <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: 2 }}>
              {engagementList.map((engagement, index) => <ModernEngagementCard engagement={engagement} key={`engagement-${index}`} />)}
              {countryDemos.length > 0 && <ModernCountryCard data={countryDemos} />}
              {genderDemos.length > 0 && <ModernGenderCard data={genderDemos} />}
            </Box>
          </SectionSurface>
        </Box>
      </Container>

      {/* Snackbar */}
      <Snackbar open={snackbarOpen} autoHideDuration={4000} onClose={(e, reason) => reason !== 'clickaway' && setSnackbarOpen(false)} anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}>
        <Alert onClose={() => setSnackbarOpen(false)} severity={snackbarSeverity} variant="filled" sx={{ width: '100%', fontSize: '1rem', fontWeight: 600 }}>{snackbarMessage}</Alert>
      </Snackbar>

      {/* Dialogs */}
      <BookingDialog open={bookingDialogOpen} onClose={() => setBookingDialogOpen(false)} creator={creatorData?.creator} isCelebrity={isCelebrity} />
      <LeaveReview open={reviewDialogOpen} onClose={() => setReviewDialogOpen(false)} creatorId={creatorId} />

      {/* T2A Universal Dialog */}
      <T2AUniversalDialog
        open={t2aOpen}
        onClose={() => setT2aOpen(false)}
        mode={t2aMode}
        recipientName={`@${creatorId}`}
        recipientGlobalUserId={null}
        resolveRecipientGUId={resolveRecipientGUId}
      />
    </ThemeProvider>
  );
}